import dataProvider, { fetchAllData, sleep } from "../../dataProvider";
import { cloneDeep } from "lodash";
import { assignmentArrivalStatusType, AssignmentReportTypeEnum } from "./const";
import haversine from "haversine";
import moment from "moment/moment";

interface IAreaDefinition {
  name: string;
  minRange: number;
  maxRange: number;
  totalPassenger: number;
}

const assignmentArrivalStatusTypeFormatter = (
  id: string,
  source: any
): string => {
  const item = source.find((type: any) => type.id === id);
  return item ? item.name : "-";
};

/**
 * Transforms an assignment record into a structured format
 * for use in an Assignment Area Report (Export CSV)
 *
 * @param record The current assignment record being processed
 * @param areas - Areas with min/max range boundaries for passenger calculation.
 * @param subcontractorAreas - Subcontractor areas with min/max range boundaries for passenger calculation.
 * @param signal Signal to allow operation cancellation
 */
const areaReportTransform = (
  record: IAssignment,
  areas: IAreaDefinition[],
  subcontractorAreas: IAreaDefinition[],
  signal: AbortSignal
) => {
  // throw an error if the operation is canceled
  if (signal.aborted) {
    throw new Error("Operation aborted");
  }

  // School location used as the origin point
  const schoolLocation = {
    latitude: record.school.lat,
    longitude: record.school.lng,
  };

  // Loop through each pickup/drop-off location to calculate distance and assign total passengers to each area / subcontractor area
  record.locations.forEach((location: IAssignmentLocation) => {
    const distance = haversine(
      schoolLocation,
      {
        latitude: location.lat,
        longitude: location.lng,
      },
      { unit: "meter" }
    );

    areas.forEach((area) => {
      if (distance >= area.minRange && distance <= area.maxRange) {
        area.totalPassenger = (area.totalPassenger || 0) + (location.passengers?.length || 0);
      }
    });

    subcontractorAreas.forEach((area) => {
      if (distance >= area.minRange && distance <= area.maxRange) {
        area.totalPassenger = (area.totalPassenger || 0) + (location.passengers?.length || 0);
      }
    });
  });

  // Create the output row for CSV with route name and total passenger for each area
  const csvRow: any = {
    "Route Name": record.routeName,
  };

  // Add total passenger for each area (If any)
  areas.forEach((area) => {
    csvRow[area.name] = area.totalPassenger
  });

  // Add total passenger for each subcontractor area (If any)
  subcontractorAreas.forEach((area) => {
    csvRow[area.name] = area.totalPassenger
  });

  // Return transformed object with the required fields for the csv row
  return csvRow;
};

/**
 * Transforms an assignment record into a structured format
 * for use in an AM Bus Arrival Report (Export CSV)
 *
 * @param record The current assignment record being processed
 * @param signal Signal to allow operation cancellation
 */
const amBusArrivalReportTransform = async (
  record: IAssignment,
  signal: AbortSignal
) => {
  // throw an error if the operation is canceled
  if (signal.aborted) {
    throw new Error("Operation aborted");
  }

  try {
    // Prepare a list of Promises for parallel fetching of vehicle, driver, and bus assistant
    const fetchPromises: Promise<any>[] = [];

    // Get vehicle details if `vehicleId` is not null or undefined
    // Otherwise push resolved Promise with null to preserve index in the array
    if (record.vehicleId) {
      fetchPromises.push(dataProvider.getOne("vehicles", { id: record.vehicleId }));
    } else {
      fetchPromises.push(Promise.resolve({ data: null }));
    }

    // Get driver details if `driverId` is not null or undefined
    // Otherwise push resolved Promise with null to preserve index in the array
    if (record.driverId) {
      fetchPromises.push(dataProvider.getOne("drivers", { id: record.driverId }));
    } else {
      fetchPromises.push(Promise.resolve({ data: null }));
    }

    // Get bus assistant details if `busAssistantId` is not null or undefined
    // Otherwise push resolved Promise with null to preserve index in the array
    if (record.busAssistantId) {
      fetchPromises.push(dataProvider.getOne("busAssistants", { id: record.busAssistantId }));
    } else {
      fetchPromises.push(Promise.resolve({ data: null }));
    }

    // Execute all fetch operations concurrently
    const [vehicleResponse, driverResponse, assistantResponse] = await Promise.all(fetchPromises);

    // Extract data from each response, if available
    const vehicle = vehicleResponse?.data;
    const driver = driverResponse?.data;
    const busAssistant = assistantResponse?.data;

    // Return transformed object with the required fields for the csv row
    return {
      assignmentId: record.id,
      waveName: record.waveName,
      routeName: record.routeName,
      vehicleNo: vehicle?.plateNumber ?? "-",
      driver: driver?.fullName ?? "-",
      busAssistant: busAssistant?.fullName ?? "-",
      assignmentDate: moment(record.estimatedStartedAt).format("YYYY-MM-DD"),
      estimatedArrivalTimeToSchool: moment(record.estimatedBusScheduleTime, "HH:mm").format("HH:mm"),
      actualArrivalTimeToSchool: record.endedAt ? moment(record.endedAt).format("HH:mm") : "-",
      arrivalStatus: record.arrivalStatus
        ? assignmentArrivalStatusTypeFormatter(
          record.arrivalStatus,
          assignmentArrivalStatusType
        )
        : "-",
    };
  } catch (error) {
    throw new Error(`Failed to fetch assignment record. ${error.message || "Unknown error"}. Please try again in a few moments`);
  }
};

const assignmentSummaryReportTransform = async (
  record: IBusSchedulePlans,
  busSchedulePlanWaveNameMap: Record<number, string>,
  startDate: string,
  endDate: string,
  signal: AbortSignal,
) => {
  const waveName = busSchedulePlanWaveNameMap[record.id];
  if (!waveName) {
    return null;
  }

  // throw an error if the operation is canceled
  if (signal.aborted) {
    throw new Error("Operation aborted");
  }

  try {
    const assignmentSummaryResponse = await dataProvider.customGetListWithStaticParams(
      `summaries/assignment?BusSchedulePlanId=${record.id}&StartDate=${startDate}&EndDate=${endDate}`
    );

    return {
      waveName: waveName,
      ...assignmentSummaryResponse.data,
    };
  } catch (error) {
    throw new Error(`Failed to fetch assignment summary record. ${error.message || "Unknown error"}. Please try again in a few moments`);
  }
};

/**
 * Processes records for CSV export, chunking data and fetching additional information as needed
 * based on the specified resource type. A callback function is called to track loading progress.
 *
 * @param body - A form data object.
 * @param records - An array of records to process.
 * @param callback - A function to handle the loading state and progress updates.
 * @param enableChunking - Optional flag to enable/disable chunking (default: true).
 * @param chunkSize - Optional size of each chunk batch (default: 100).
 * @returns An array containing processed data ready for CSV export.
 */
export const processAssignmentRecordsForCsv = async (
  body: any,
  records: any[],
  callback: (progressUpdate: {
    isLoading?: boolean;
    loadedRecordCount: number;
    totalRecords: number;
  }) => void,
  enableChunking: boolean = true,
  chunkSize: number = 100
) => {
  // Split the records array into chunks of the specified size,
  // if chunking is enabled; otherwise, process everything at once
  const chunks = enableChunking
    ? Array.from(
      { length: Math.ceil(records.length / chunkSize) },
      (_, i) => records.slice(i * chunkSize, i * chunkSize + chunkSize)
    )
    : [records];

  let processedData: any[] = [];

  // Create an abort controller to handle cancellation of tasks
  const abortController = new AbortController();
  const signal = abortController.signal;

  // Create a mapping: busSchedulePlanId -> waveName using records and busSchedulePlans
  let busSchedulePlanWaveNameMap: Record<number, string> = {};

  // Initialize area configurations based on school and report type
  let areas: IAreaDefinition[] = [];
  let subcontractorAreas: IAreaDefinition[] = [];
  if (body.reportType === AssignmentReportTypeEnum.AreaReport) {
    // get area data
    const areaResponse = await fetchAllData("areas", {
      SchoolId: body.school.id,
    },{
      field: "MaxDistanceInMeters",
      order: "asc"
    });

    // Check if the school ID is 5 (TWS)
    if (body.school.id !== 5) {
      areas = areaResponse.map((area, index) => ({
        name: area.name,
        minRange: index > 0 ? areaResponse[index - 1].maxDistanceInMeters : 0,
        maxRange: area.maxDistanceInMeters,
        totalPassenger: 0,
      }));
    } else {
      // Format the area name with range labels
      areas = areaResponse.map((area, index) => {
        const minRange = index > 0 ? areaResponse[index - 1].maxDistanceInMeters : 0;
        const minRangeInKm = minRange / 1000;
        const maxRangeInKm = area.maxDistanceInMeters / 1000;
        return {
          name: `${area.name} (${minRangeInKm.toFixed(1)}km - ${maxRangeInKm.toFixed(1)}km)`,
          minRange,
          maxRange: area.maxDistanceInMeters,
          totalPassenger: 0,
        }
      });

      // Additional subcontractor area zones (2km intervals)
      for (let i = 0; i < 28; i++) {
        const minRange = i * 2 * 1000;
        const maxRange = (i + 1) * 2 * 1000;

        const minRangeInKm = minRange / 1000;
        const maxRangeInKm = maxRange / 1000;

        subcontractorAreas.push({
          name: `SubCon Area ${i + 1} (${minRangeInKm.toFixed(1)}km - ${maxRangeInKm.toFixed(1)}km)`,
          minRange,
          maxRange,
          totalPassenger: 0,
        });
      }
    }
  }

  if (body.reportType === AssignmentReportTypeEnum.AssignmentSummaryReport) {
    // Get all busSchedulePlans
    const busSchedules = await fetchAllData("busSchedules", {
      SchoolId: body.school.id,
      Year: body.year,
      Month: body.month,
    });

    busSchedules.forEach((schedule) => {
      const matchingRecord = records.find((record) => record.busScheduleId === schedule.id);
      if (matchingRecord) {
        busSchedulePlanWaveNameMap[matchingRecord.id] = schedule.name;
      }
    });
  }

  // Loop over each chunk to process records in manageable batches
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];

    // Process all records within the chunk concurrently
    const chunkResults = await Promise.all(
      chunk.map(async (record, recordIndex) => {
        // Exit if the operation is canceled
        if (signal.aborted) return;

        // Optional delay each request by 100ms * recordIndex within the chunk,
        // Only if chunking
        if (enableChunking) {
          await sleep(recordIndex * 100);
        }

        // Calculate the current record index for progress tracking and
        // update the progress using the callback
        const currentIndex = chunkIndex * chunkSize + recordIndex + 1;
        callback({
          loadedRecordCount: currentIndex,
          totalRecords: records.length,
        });

        // Process records based on the export type
        switch (body.reportType) {
          case AssignmentReportTypeEnum.AreaReport:
            return areaReportTransform(
              record,
              cloneDeep(areas),
              cloneDeep(subcontractorAreas),
              signal
            );
          case AssignmentReportTypeEnum.PassengerReport:
            return record;
          case AssignmentReportTypeEnum.AmBusArrivalReport:
            return amBusArrivalReportTransform(
              record,
              signal
            );
          case AssignmentReportTypeEnum.AssignmentSummaryReport:
            return assignmentSummaryReportTransform(
              record,
              busSchedulePlanWaveNameMap,
              body.startDate,
              body.endDate,
              signal
            );
          default:
            return null;
        }
      })
    );

    // Remove null values first
    const validResults = chunkResults.filter(result => result !== null);

    // Flatten the array to ensure it's a single-dimensional array
    const flattenedResults = validResults.flat();

    // Append processed chunk results to the main processed data array
    processedData = [...processedData, ...flattenedResults];
  }

  // Return the fully processed data for CSV export
  return processedData;
};